<template>
  <HrPageLayout
    title="Skills Audit Assessment"
    :subtitle="pageSubtitle"
    icon=""
    :breadcrumb-items="breadcrumbItems"
  >
    <!-- Submissions list -->
    <div v-if="pageMode === 'list'" class="ska-list">
      <div class="ska-hero">
        <p class="ska-lead">
          Access your Skills Audit Assessment submissions by audit year. <strong>Draft</strong> and <strong>returned</strong> forms can be edited.
          <strong>Submitted</strong> forms are view-only until your supervisor or HR returns them for correction.
        </p>
      </div>
      <div class="ska-list-toolbar">
        <button type="button" class="ska-btn ska-btn--primary ska-btn-new-blink" @click="startNewAssessment">
          <i class="bx bx-plus-circle"></i> New assessment
        </button>
      </div>
      <div v-if="loadingList" class="ska-muted">Loading your records…</div>
      <div v-else class="ska-table-wrap">
        <table class="ska-table" aria-label="My skills audit submissions">
          <thead>
            <tr>
              <th>Audit year</th>
              <th>Status</th>
              <th>Last updated</th>
              <th>Submitted</th>
              <th class="ska-th-actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in myRecords" :key="r.id">
              <td class="ska-year">{{ r.audit_year }}</td>
              <td><span class="ska-pill" :class="statusPillClass(r.status)">{{ formatStatus(r.status) }}</span></td>
              <td>{{ formatDt(r.updated_at) }}</td>
              <td>{{ r.submitted_at ? formatDt(r.submitted_at) : "—" }}</td>
              <td class="ska-actions">
                <button
                  v-if="isEditableStatus(r.status)"
                  type="button"
                  class="ska-btn ska-btn--sm ska-btn--outline"
                  @click="openRecord(r)"
                >
                  Edit
                </button>
                <button
                  v-else
                  type="button"
                  class="ska-btn ska-btn--sm ska-btn--ghost"
                  @click="openRecord(r)"
                >
                  View
                </button>
              </td>
            </tr>
            <tr v-if="!myRecords.length">
              <td colspan="5" class="ska-empty">You have no saved assessments yet. Click <strong>New assessment</strong> to begin.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Assessment form -->
    <div v-else ref="skaFormTop" class="ska-form">
      <div class="card mb-3 ska-toolbar-card">
        <div
          v-if="statusMsg"
          class="card-body border-bottom py-3 ska-feedback"
          :class="statusOk ? 'ska-feedback--success' : 'ska-feedback--error'"
          role="status"
        >
          <i v-if="statusOk" class="bx bx-check-circle ska-feedback-icon"></i>
          <span>{{ statusMsg }}</span>
        </div>
        <div class="card-body row g-3 align-items-end">
          <div class="col-md-4">
            <button type="button" class="ska-btn ska-btn--ghost" @click="backToList">
              <i class="bx bx-arrow-back"></i> My submissions
            </button>
          </div>
          <div class="col-md-4">
            <label class="form-label">Audit year</label>
            <select v-model.number="auditYear" class="form-select" @change="reload">
              <option v-for="y in yearChoices" :key="'y-' + y" :value="y">{{ y }}</option>
            </select>
          </div>
          <div class="col-md-4 text-md-end">
            <span v-if="recordStatus" class="ska-pill ska-pill--inline" :class="statusPillClass(recordStatus)">{{ formatStatus(recordStatus) }}</span>
          </div>
        </div>
      </div>

      <div v-if="recordStatus === 'Returned' && returnReason" class="alert alert-info mb-3" role="status">
        <strong>Returned for correction.</strong>
        <span class="d-block mt-1">{{ returnReason }}</span>
      </div>

      <div v-if="formLocked" class="alert alert-warning mb-3">
        <strong>View only.</strong> Status: <strong>{{ recordStatus.replace(/_/g, " ") }}</strong>.
        You can edit again only after your supervisor or authorising officer returns the form for correction.
      </div>

      <div class="card ska-scroll position-relative">
        <div v-if="formLocked" class="saas-lock-overlay" title="Editing locked until HR/supervisor/chief returns for correction"></div>
        <div class="card-body">
          <p class="small text-danger mb-2">
            All fields are compulsory (*). Fill every field before submitting.
          </p>
          <!-- SECTION 1 -->
          <h4 class="section-title">Section 1. Personal information</h4>
          <div class="row g-2 mb-2">
            <div class="col-md-4"><label class="small text-muted">Surname</label><input v-model="form.section1.surname" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">Middle name</label><input v-model="form.section1.middleName" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">Other names</label><input v-model="form.section1.otherNames" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">ID No.</label><input v-model="form.section1.idNo" class="form-control form-control-sm" readonly tabindex="-1"></div>
            <div class="col-md-4"><label class="small text-muted">Date of birth</label><input v-model="form.section1.dateOfBirth" type="date" class="form-control form-control-sm" readonly tabindex="-1"></div>
            <div class="col-md-4"><label class="small text-muted">Gender</label><input v-model="form.section1.gender" class="form-control form-control-sm" readonly tabindex="-1"></div>
            <div class="col-md-4"><label class="small text-muted">Phone</label><input v-model="form.section1.phoneNo" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">KRA PIN</label><input v-model="form.section1.kraPin" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">Ethnicity</label><input v-model="form.section1.ethnicity" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">Home county</label><input v-model="form.section1.homeCounty" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">Sub-county</label><input v-model="form.section1.subCounty" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">Ward</label><input v-model="form.section1.ward" class="form-control form-control-sm"></div>
            <div class="col-md-6">
              <label class="small text-muted">PWD — tick</label>
              <div :class="{ 'ska-required-missing-group': validationAttempted && !String(form.section1.pwdYesNo || '').trim() }">
                <label class="me-3"><input v-model="form.section1.pwdYesNo" type="radio" value="YES"> Yes</label>
                <label><input v-model="form.section1.pwdYesNo" type="radio" value="NO"> No</label>
              </div>
            </div>
            <div class="col-md-6"><label class="small text-muted">If yes (specify nature)</label><input v-model="form.section1.pwdNature" class="form-control form-control-sm"></div>
            <div class="col-md-6"><label class="small text-muted">NCPWD cert. no.</label><input v-model="form.section1.ncpwdCertNo" class="form-control form-control-sm"></div>
            <div class="col-md-6"><label class="small text-muted">KRA exemption cert. no.</label><input v-model="form.section1.kraExemptionCertNo" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">Next of kin</label><input v-model="form.section1.nokName" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">Relationship</label><input v-model="form.section1.nokRelationship" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">Phone</label><input v-model="form.section1.nokPhone" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">Alternative NOK</label><input v-model="form.section1.altNokName" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">Relationship</label><input v-model="form.section1.altNokRelationship" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">Phone</label><input v-model="form.section1.altNokPhone" class="form-control form-control-sm"></div>
          </div>

          <!-- SECTION 2 -->
          <h4 class="section-title mt-4">Section 2. Work related information</h4>
          <div class="row g-2 mb-2">
            <div class="col-md-4"><label class="small text-muted">Employee number</label><input v-model="form.section2.employeeNumber" class="form-control form-control-sm" readonly tabindex="-1"></div>
            <div class="col-md-4"><label class="small text-muted">Pay group</label><input v-model="form.section2.payGroup" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">Designation (payroll)</label><input v-model="form.section2.designationPayroll" class="form-control form-control-sm"></div>
            <div class="col-md-6"><label class="small text-muted">Current deployment (title)</label><input v-model="form.section2.currentDeployment" class="form-control form-control-sm"></div>
            <div class="col-md-3"><label class="small text-muted">Date of 1st appointment</label><input v-model="form.section2.dateFirstAppointment" type="date" class="form-control form-control-sm"></div>
            <div class="col-md-3"><label class="small text-muted">Job group at 1st appointment</label><input v-model="form.section2.jobGroupFirst" class="form-control form-control-sm"></div>
            <div class="col-md-3"><label class="small text-muted">Date current appointment / last promotion</label><input v-model="form.section2.dateCurrentAppointment" type="date" class="form-control form-control-sm"></div>
            <div class="col-md-3"><label class="small text-muted">Current job group</label><input v-model="form.section2.currentJobGroup" class="form-control form-control-sm"></div>
            <div class="col-md-6"><label class="small text-muted">Department / agency</label><input v-model="form.section2.departmentAgency" class="form-control form-control-sm"></div>
            <div class="col-md-6"><label class="small text-muted">Directorate / section / division</label><input v-model="form.section2.directorate" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">Work station</label><input v-model="form.section2.workStation" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">Years in current duty station</label><input v-model="form.section2.yearsCurrentStation" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small text-muted">Years in public service</label><input v-model="form.section2.yearsPublicService" class="form-control form-control-sm"></div>
            <div class="col-md-6"><label class="small text-muted">Date of retirement / end of contract</label><input v-model="form.section2.retirementOrContractEnd" type="date" class="form-control form-control-sm"></div>
          </div>

          <!-- SECTION 3 -->
          <h4 class="section-title mt-4">Section 3. Academic & professional qualifications</h4>
          <div class="table-responsive mb-2"><table class="table table-sm table-bordered align-middle">
            <thead><tr><th>Year</th><th>Course</th><th>Institution</th><th>Level / qualification</th><th>Cert. no.</th><th>Academic / Professional</th></tr></thead>
            <tbody><tr v-for="(r, i) in form.section3" :key="'s3-' + i">
              <td><input v-model="r.year" class="form-control form-control-sm"></td>
              <td><input v-model="r.course" class="form-control form-control-sm"></td>
              <td><input v-model="r.institution" class="form-control form-control-sm"></td>
              <td><input v-model="r.level" class="form-control form-control-sm"></td>
              <td><input v-model="r.certNo" class="form-control form-control-sm"></td>
              <td><input v-model="r.acadProf" class="form-control form-control-sm" placeholder="A / P"></td>
            </tr></tbody>
          </table></div>

          <!-- SECTION 4 -->
          <h4 class="section-title mt-4">Section 4. Professional bodies</h4>
          <div class="table-responsive mb-2"><table class="table table-sm table-bordered align-middle">
            <thead><tr><th>Body</th><th>Certification</th><th>Registration / cert. no.</th><th>Validity / expiry</th></tr></thead>
            <tbody><tr v-for="(r, i) in form.section4" :key="'s4-' + i">
              <td><input v-model="r.body" class="form-control form-control-sm"></td>
              <td><input v-model="r.certification" class="form-control form-control-sm"></td>
              <td><input v-model="r.regNo" class="form-control form-control-sm"></td>
              <td><input v-model="r.validity" class="form-control form-control-sm"></td>
            </tr></tbody>
          </table></div>

          <!-- SECTION 5 -->
          <h4 class="section-title mt-4">Section 5. Other trainings (last 5 years)</h4>
          <div class="table-responsive mb-2"><table class="table table-sm table-bordered align-middle">
            <thead><tr><th>Period</th><th>Institution</th><th>Sponsorship</th><th>Course</th><th>Qualification</th></tr></thead>
            <tbody><tr v-for="(r, i) in form.section5" :key="'s5-' + i">
              <td><input v-model="r.period" class="form-control form-control-sm"></td>
              <td><input v-model="r.institution" class="form-control form-control-sm"></td>
              <td><input v-model="r.sponsorship" class="form-control form-control-sm"></td>
              <td><input v-model="r.course" class="form-control form-control-sm"></td>
              <td><input v-model="r.qualification" class="form-control form-control-sm"></td>
            </tr></tbody>
          </table></div>

          <!-- SECTION 6 -->
          <h4 class="section-title mt-4">Section 6. Training needs (priority)</h4>
          <div class="table-responsive mb-2"><table class="table table-sm table-bordered align-middle">
            <thead><tr><th>#</th><th>Course / programme</th><th>Proposed institution</th></tr></thead>
            <tbody><tr v-for="(r, i) in form.section6" :key="'s6-' + i">
              <td>{{ i + 1 }}</td>
              <td><input v-model="r.course" class="form-control form-control-sm"></td>
              <td><input v-model="r.institution" class="form-control form-control-sm"></td>
            </tr></tbody>
          </table></div>

          <!-- SECTION 7 -->
          <h4 class="section-title mt-4">Section 7. Work experience</h4>
          <label class="small fw-bold">A) Current duties</label>
          <textarea v-model="form.section7.currentDuties" class="form-control mb-2" rows="4"></textarea>
          <label class="small fw-bold">B) Duties to delete from schedule</label>
          <textarea v-model="form.section7.dutiesToDelete" class="form-control mb-2" rows="3"></textarea>
          <label class="small fw-bold">C) Previous experience</label>
          <div class="table-responsive mb-2"><table class="table table-sm table-bordered align-middle">
            <thead><tr><th>From</th><th>To</th><th>Institution</th><th>Position</th></tr></thead>
            <tbody><tr v-for="(r, i) in form.section7.previous" :key="'s7-' + i">
              <td><input v-model="r.from" type="date" class="form-control form-control-sm"></td>
              <td><input v-model="r.to" type="date" class="form-control form-control-sm"></td>
              <td><input v-model="r.institution" class="form-control form-control-sm"></td>
              <td><input v-model="r.position" class="form-control form-control-sm"></td>
            </tr></tbody>
          </table></div>

          <!-- SECTION 8 -->
          <h4 class="section-title mt-4">Section 8. Skills information</h4>
          <p class="small text-muted">Job-specific / hard skills by category</p>
          <div class="row g-2 mb-2">
            <div class="col-md-6"><label class="small">Administrative</label><textarea v-model="form.section8.hard.administrative" class="form-control form-control-sm" rows="2"></textarea></div>
            <div class="col-md-6"><label class="small">Managerial</label><textarea v-model="form.section8.hard.managerial" class="form-control form-control-sm" rows="2"></textarea></div>
            <div class="col-md-6"><label class="small">Technical</label><textarea v-model="form.section8.hard.technical" class="form-control form-control-sm" rows="2"></textarea></div>
            <div class="col-md-6"><label class="small">RPL</label><textarea v-model="form.section8.hard.rpl" class="form-control form-control-sm" rows="2"></textarea></div>
          </div>
          <label class="small fw-bold">Soft skills</label>
          <textarea v-model="form.section8.softSkills" class="form-control mb-3" rows="4"></textarea>

          <!-- SECTION 9 -->
          <h4 class="section-title mt-4">Section 9. Additional information</h4>
          <div class="mb-2"><label class="small">1) Major roles / core mandate of your directorate</label><textarea v-model="form.section9.q1" class="form-control" rows="3"></textarea></div>
          <div class="mb-2"><label class="small">2) How your job contributes to the mandate</label><textarea v-model="form.section9.q2" class="form-control" rows="3"></textarea></div>
          <div class="mb-2"><label class="small">3) Other roles you would be interested in</label><textarea v-model="form.section9.q3" class="form-control" rows="3"></textarea></div>
          <div class="mb-2"><label class="small">4) Challenges hindering performance</label><textarea v-model="form.section9.q4" class="form-control" rows="4"></textarea></div>
          <div class="mb-2"><label class="small">5) Other relevant information</label><textarea v-model="form.section9.q5" class="form-control" rows="4"></textarea></div>

          <!-- Declaration -->
          <h4 class="section-title mt-4">Declaration</h4>
          <div class="row g-2 align-items-end">
            <div class="col-md-6"><label class="small">I (full name)</label><input v-model="form.declaration.fullName" class="form-control form-control-sm"></div>
            <div class="col-md-6"><label class="small">ID No.</label><input v-model="form.declaration.idNo" class="form-control form-control-sm" readonly tabindex="-1"></div>
          </div>
        </div>
      </div>

      <div class="card ska-footer-card mt-3">
        <div class="card-body ska-footer-inner">
          <p class="ska-footer-hint mb-3 mb-md-0">
            Save progress as <strong>draft</strong>, or <strong>submit</strong> when complete. Submitted assessments lock until returned for correction.
          </p>
          <div class="ska-footer-btns">
            <button type="button" class="ska-btn ska-btn--outline ska-btn--lg" :disabled="saving || formLocked" @click="saveDraft">{{ saving ? "Saving…" : "Save draft" }}</button>
            <button type="button" class="ska-btn ska-btn--primary ska-btn--lg" :disabled="saving || !canSubmit || formLocked" @click="submitForm">{{ saving ? "Submitting…" : "Submit assessment" }}</button>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, toast } from "@/api";

function lockedStatuses() {
  return ["Submitted", "Supervisor_Signed", "Chief_Authorised"];
}

function emptyS3() {
  return { year: "", course: "", institution: "", level: "", certNo: "", acadProf: "" };
}
function emptyS4() {
  return { body: "", certification: "", regNo: "", validity: "" };
}
function emptyS5() {
  return { period: "", institution: "", sponsorship: "", course: "", qualification: "" };
}
function emptyS6() {
  return { course: "", institution: "" };
}
function emptyS7prev() {
  return { from: "", to: "", institution: "", position: "" };
}

function makeEmptyForm() {
  const s3 = [];
  const s4 = [];
  const s5 = [];
  const s6 = [];
  const prev = [];
  for (let i = 0; i < 5; i++) {
    s3.push(emptyS3());
    s4.push(emptyS4());
    s5.push(emptyS5());
  }
  for (let i = 0; i < 4; i++) s6.push(emptyS6());
  for (let i = 0; i < 4; i++) prev.push(emptyS7prev());

  return {
    section1: {
      surname: "",
      middleName: "",
      otherNames: "",
      idNo: "",
      dateOfBirth: "",
      gender: "",
      phoneNo: "",
      kraPin: "",
      ethnicity: "",
      homeCounty: "",
      subCounty: "",
      ward: "",
      pwdYesNo: "",
      pwdNature: "",
      ncpwdCertNo: "",
      kraExemptionCertNo: "",
      nokName: "",
      nokRelationship: "",
      nokPhone: "",
      altNokName: "",
      altNokRelationship: "",
      altNokPhone: ""
    },
    section2: {
      employeeNumber: "",
      payGroup: "",
      designationPayroll: "",
      currentDeployment: "",
      dateFirstAppointment: "",
      jobGroupFirst: "",
      dateCurrentAppointment: "",
      currentJobGroup: "",
      departmentAgency: "",
      directorate: "",
      workStation: "",
      yearsCurrentStation: "",
      yearsPublicService: "",
      retirementOrContractEnd: ""
    },
    section3: s3,
    section4: s4,
    section5: s5,
    section6: s6,
    section7: {
      currentDuties: "",
      dutiesToDelete: "",
      previous: prev
    },
    section8: {
      hard: { administrative: "", managerial: "", technical: "", rpl: "" },
      softSkills: ""
    },
    section9: { q1: "", q2: "", q3: "", q4: "", q5: "" },
    declaration: { fullName: "", idNo: "" }
  };
}

function deepMerge(target, src) {
  if (!src || typeof src !== "object") return target;
  Object.keys(src).forEach((k) => {
    const v = src[k];
    if (v && typeof v === "object" && !Array.isArray(v)) {
      if (!target[k]) target[k] = {};
      deepMerge(target[k], v);
    } else if (Array.isArray(v) && Array.isArray(target[k])) {
      v.forEach((item, i) => {
        if (target[k][i] && typeof item === "object") deepMerge(target[k][i], item);
        else target[k][i] = item;
      });
    } else {
      target[k] = v;
    }
  });
  return target;
}

function firstMissingFieldPath(value, currentPath = "") {
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const nextPath = currentPath ? `${currentPath}.${i}` : String(i);
      const found = firstMissingFieldPath(value[i], nextPath);
      if (found) return found;
    }
    return "";
  }

  if (value && typeof value === "object") {
    const keys = Object.keys(value);
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      const nextPath = currentPath ? `${currentPath}.${k}` : k;
      const found = firstMissingFieldPath(value[k], nextPath);
      if (found) return found;
    }
    return "";
  }

  if (value === null || value === undefined) return currentPath;
  if (typeof value === "string" && value.trim() === "") return currentPath;
  return "";
}

function friendlyFieldLabel(path) {
  if (!path) return "A required field";
  const map = {
    "section1.surname": "Surname",
    "section1.middleName": "Middle name",
    "section1.otherNames": "Other names",
    "section1.idNo": "ID No.",
    "section1.dateOfBirth": "Date of birth",
    "section1.gender": "Gender",
    "section1.phoneNo": "Phone",
    "section1.kraPin": "KRA PIN",
    "section1.ethnicity": "Ethnicity",
    "section1.homeCounty": "Home county",
    "section1.subCounty": "Sub-county",
    "section1.ward": "Ward",
    "section1.pwdYesNo": "PWD selection",
    "section1.pwdNature": "PWD nature",
    "section1.ncpwdCertNo": "NCPWD cert. no.",
    "section1.kraExemptionCertNo": "KRA exemption cert. no.",
    "section1.nokName": "Next of kin",
    "section1.nokRelationship": "NOK relationship",
    "section1.nokPhone": "NOK phone",
    "section1.altNokName": "Alternative NOK",
    "section1.altNokRelationship": "Alternative NOK relationship",
    "section1.altNokPhone": "Alternative NOK phone",
    "declaration.fullName": "Declaration full name",
    "declaration.idNo": "Declaration ID No."
  };
  if (map[path]) return map[path];

  const p = path.split(".");
  const last = p[p.length - 1] || path;
  if (/^\d+$/.test(last)) return `Row ${Number(last) + 1}`;
  return last
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase());
}

export default {
  name: "EmployeeSkillsAuditAssessment",
  components: { HrPageLayout },
  data() {
    const y = new Date().getFullYear();
    return {
      pageMode: "list",
      myRecords: [],
      loadingList: false,
      auditYear: y,
      yearChoices: [],
      form: makeEmptyForm(),
      recordStatus: "",
      returnReason: "",
      saving: false,
      statusMsg: "",
      statusOk: false,
      validationAttempted: false
    };
  },
  computed: {
    pageSubtitle() {
      return this.pageMode === "list"
        ? "County workforce skills declaration — view past submissions or start a new assessment."
        : "Complete all sections. Fields from your HR profile are prefilled where available.";
    },
    breadcrumbItems() {
      return [
        { label: "Home", route: "/employee-dashboard" },
        { label: "Employee Self-Service" },
        { label: "Skills Audit Assessment" }
      ];
    },
    canSubmit() {
      const st = String(this.recordStatus || "");
      return st === "" || st === "Draft" || st === "Returned";
    },
    formLocked() {
      const st = String(this.recordStatus || "");
      return lockedStatuses().includes(st);
    }
  },
  created() {
    const y = new Date().getFullYear();
    this.yearChoices = [y + 1, y, y - 1, y - 2];
    this.loadMyRecords();
  },
  mounted() {
    this.$nextTick(() => this.highlightMissingFields());
  },
  methods: {
    async loadMyRecords() {
      this.loadingList = true;
      try {
        const fd = new FormData();
        fd.append("function", "listMySkillsAuditAssessments");
        const res = await execute(fd, authUrl);
        this.myRecords = res?.data?.success && Array.isArray(res?.data?.data?.records)
          ? res.data.data.records
          : [];
      } catch (_) {
        this.myRecords = [];
      } finally {
        this.loadingList = false;
      }
    },
    startNewAssessment() {
      const y = new Date().getFullYear();
      this.auditYear = y;
      this.pageMode = "form";
      this.statusMsg = "";
      this.reload();
    },
    openRecord(row) {
      if (!row || row.audit_year == null) return;
      this.auditYear = Number(row.audit_year);
      this.pageMode = "form";
      this.statusMsg = "";
      this.reload();
    },
    backToList() {
      this.pageMode = "list";
      this.statusMsg = "";
      this.loadMyRecords();
    },
    formatStatus(s) {
      const k = String(s || "");
      const map = {
        Draft: "Draft",
        Submitted: "Submitted",
        Supervisor_Signed: "Supervisor signed",
        Chief_Authorised: "Chief authorised",
        Returned: "Returned for correction"
      };
      return map[k] || k.replace(/_/g, " ") || "—";
    },
    statusPillClass(s) {
      const k = String(s || "");
      if (k === "Draft") return "ska-pill--draft";
      if (k === "Returned") return "ska-pill--returned";
      if (k === "Submitted") return "ska-pill--submitted";
      if (k === "Supervisor_Signed") return "ska-pill--supervisor";
      if (k === "Chief_Authorised") return "ska-pill--chief";
      return "ska-pill--muted";
    },
    formatDt(v) {
      if (v == null || v === "") return "—";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return String(v);
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    },
    isEditableStatus(st) {
      const k = String(st || "");
      return k === "Draft" || k === "Returned";
    },
    scrollFormToTop() {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      this.$nextTick(() => {
        const el = this.$refs.skaFormTop;
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    async reload() {
      this.statusMsg = "";
      await this.loadPrefillAndDraft();
    },
    async loadPrefillAndDraft() {
      try {
        const fd1 = new FormData();
        fd1.append("function", "getMySkillsAuditAssessmentPrefill");
        const pr = await execute(fd1, authUrl);
        const prefill = pr.data?.success ? pr.data.data?.prefill : null;

        const fd2 = new FormData();
        fd2.append("function", "getMySkillsAuditAssessment");
        fd2.append("audit_year", String(this.auditYear));
        const dr = await execute(fd2, authUrl);
        const payload = dr.data?.success ? dr.data.data : {};
        const saved = payload.form && typeof payload.form === "object" ? payload.form : {};
        const rec = payload.record;

        const base = makeEmptyForm();
        if (prefill && typeof prefill === "object") {
          if (prefill.section1) deepMerge(base.section1, prefill.section1);
          if (prefill.section2) deepMerge(base.section2, prefill.section2);
        }
        deepMerge(base, saved);
        this.form = base;

        if (prefill?.section1?.idNo && !String(this.form.declaration.idNo || "").trim()) {
          this.form.declaration.idNo = prefill.section1.idNo;
        }
        const fn = [this.form.section1.otherNames, this.form.section1.middleName, this.form.section1.surname].filter(Boolean).join(" ").trim();
        if (fn && !String(this.form.declaration.fullName || "").trim()) {
          this.form.declaration.fullName = fn;
        }

        this.recordStatus = rec?.status ? String(rec.status) : "";
        this.returnReason = rec?.return_reason ? String(rec.return_reason) : "";
      } catch (e) {
        this.statusMsg = "Could not load form.";
        this.statusOk = false;
      }
    },
    collectPayload() {
      return JSON.stringify(this.form);
    },
    validateAllRequiredFields() {
      const missingPath = firstMissingFieldPath(this.form);
      if (!missingPath) return true;
      this.validationAttempted = true;
      this.statusOk = false;
      this.statusMsg = `${friendlyFieldLabel(missingPath)} is required. Please complete all fields before submitting.`;
      this.$nextTick(() => {
        this.highlightMissingFields();
        this.scrollFormToTop();
      });
      return false;
    },
    highlightMissingFields() {
      const root = this.$el ? this.$el.querySelector(".ska-scroll .card-body") : null;
      if (!root) return;
      const controls = root.querySelectorAll(".form-control");
      controls.forEach((el) => {
        if (el.hasAttribute("readonly") || el.hasAttribute("disabled")) {
          el.classList.remove("ska-required-missing");
          return;
        }
        const val = String(el.value ?? "").trim();
        if (this.validationAttempted && !val) el.classList.add("ska-required-missing");
        else el.classList.remove("ska-required-missing");
      });
    },
    async saveDraft() {
      this.saving = true;
      this.statusMsg = "";
      try {
        const fd = new FormData();
        fd.append("function", "saveMySkillsAuditAssessment");
        fd.append("audit_year", String(this.auditYear));
        fd.append("form_json", this.collectPayload());
        const res = await execute(fd, authUrl);
        this.statusOk = !!res.data?.success;
        this.statusMsg = res.data?.message || (this.statusOk ? "Draft saved." : "Save failed.");
        if (this.statusOk) {
          this.recordStatus = "Draft";
          await this.loadPrefillAndDraft();
          this.loadMyRecords();
        }
      } catch (e) {
        this.statusOk = false;
        this.statusMsg = "Save failed.";
      } finally {
        this.saving = false;
      }
    },
    async submitForm() {
      if (!this.validateAllRequiredFields()) {
        return;
      }
      this.validationAttempted = false;
      this.highlightMissingFields();
      this.saving = true;
      this.statusMsg = "";
      try {
        const fd = new FormData();
        fd.append("function", "submitMySkillsAuditAssessment");
        fd.append("audit_year", String(this.auditYear));
        fd.append("form_json", this.collectPayload());
        const res = await execute(fd, authUrl);
        this.statusOk = !!res.data?.success;
        const defaultOk =
          "Your skills audit assessment was submitted successfully. Status is now Submitted (see pill on the right, or My submissions).";
        const apiMsg = res.data?.message && String(res.data.message).trim();
        this.statusMsg = apiMsg || (this.statusOk ? defaultOk : "Submit failed.");
        if (this.statusOk) {
          toast.success(apiMsg || "Skills audit assessment submitted successfully.");
          this.recordStatus = "Submitted";
          await this.loadPrefillAndDraft();
          this.loadMyRecords();
          this.$nextTick(() => this.scrollFormToTop());
        }
      } catch (e) {
        this.statusOk = false;
        this.statusMsg = "Submit failed.";
      } finally {
        this.saving = false;
      }
    }
  }
  ,
  watch: {
    form: {
      deep: true,
      handler() {
        if (!this.validationAttempted) return;
        this.$nextTick(() => this.highlightMissingFields());
      }
    }
  }
};
</script>

<style scoped>
.ska-list {
  width: 100%;
  box-sizing: border-box;
  padding-bottom: 32px;
}
.ska-hero {
  background: linear-gradient(135deg, #0d4f73 0%, #1478b0 100%);
  color: #fff;
  border-radius: 14px;
  padding: 20px 22px;
  margin-bottom: 18px;
  box-shadow: 0 8px 28px rgba(13, 79, 115, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.12);
}
.ska-lead {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  opacity: 0.96;
}
.ska-list-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
}
.ska-muted {
  color: #64748b;
  font-size: 14px;
  padding: 12px 0;
}
.ska-table-wrap {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 2px 14px rgba(13, 79, 115, 0.06);
}
.ska-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.ska-table thead {
  background: #f8fafc;
}
.ska-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
}
.ska-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  vertical-align: middle;
}
.ska-table tbody tr:hover {
  background: #f8fafc;
}
.ska-year {
  font-weight: 700;
  color: #0d4f73;
}
.ska-th-actions {
  width: 120px;
}
.ska-actions {
  text-align: right;
  white-space: nowrap;
}
.ska-empty {
  text-align: center;
  color: #94a3b8;
  padding: 28px 16px !important;
}
.ska-pill {
  display: inline-block;
  padding: 4px 11px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.ska-pill--inline {
  font-size: 12px;
  padding: 6px 12px;
}
.ska-pill--draft {
  background: #fef3c7;
  color: #92400e;
}
.ska-pill--returned {
  background: #dbeafe;
  color: #1e40af;
}
.ska-pill--submitted {
  background: #e0f2fe;
  color: #0369a1;
}
.ska-pill--supervisor {
  background: #ecfdf5;
  color: #047857;
}
.ska-pill--chief {
  background: #f0fdf4;
  color: #166534;
}
.ska-pill--muted {
  background: #f1f5f9;
  color: #64748b;
}

.ska-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s, transform 0.15s;
}
.ska-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.ska-btn--primary {
  background: linear-gradient(135deg, #2195d3 0%, #0d4f73 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(33, 149, 211, 0.35);
}
.ska-btn--primary:hover:not(:disabled) {
  box-shadow: 0 6px 18px rgba(33, 149, 211, 0.45);
}
.ska-btn-new-blink {
  animation: ska-new-assessment-blink 1.15s ease-in-out infinite;
}
.ska-btn-new-blink:hover:not(:disabled) {
  filter: brightness(1.05);
}
@keyframes ska-new-assessment-blink {
  0%,
  100% {
    box-shadow: 0 4px 14px rgba(33, 149, 211, 0.35);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 22px 6px rgba(33, 149, 211, 0.45);
    transform: scale(1.02);
  }
}
.ska-btn--outline {
  background: #fff;
  color: #0d4f73;
  border: 2px solid #94a3b8;
}
.ska-btn--outline:hover:not(:disabled) {
  border-color: #2195d3;
  color: #1478b0;
}
.ska-btn--ghost {
  background: transparent;
  color: #0d4f73;
  border: 1px solid transparent;
}
.ska-btn--ghost:hover:not(:disabled) {
  background: #f1f5f9;
}
.ska-btn--sm {
  padding: 6px 12px;
  font-size: 13px;
}
.ska-btn--lg {
  padding: 12px 22px;
  font-size: 15px;
  border-radius: 11px;
}

.ska-form {
  width: 100%;
  box-sizing: border-box;
  padding-bottom: 40px;
}
.ska-toolbar-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 12px rgba(13, 79, 115, 0.06);
}
.ska-feedback {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  line-height: 1.45;
}
.ska-feedback--success {
  background: #ecfdf5;
  border-color: #a7f3d0 !important;
  color: #065f46;
}
.ska-feedback--error {
  background: #fef2f2;
  border-color: #fecaca !important;
  color: #991b1b;
}
.ska-feedback-icon {
  font-size: 22px;
  flex-shrink: 0;
  margin-top: 1px;
}
.ska-scroll { max-height: none; }
.saas-lock-overlay {
  position: absolute;
  inset: 0;
  z-index: 4;
  background: rgba(255, 255, 255, 0.65);
  cursor: not-allowed;
}
.ska-footer-card {
  border-radius: 14px;
  border: 1px solid #bae6fd;
  background: linear-gradient(180deg, #ffffff 0%, #f0f9ff 100%);
  box-shadow: 0 4px 20px rgba(13, 79, 115, 0.08);
}
.ska-footer-inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.ska-footer-hint {
  flex: 1;
  min-width: 220px;
  margin: 0;
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
}
.ska-footer-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.section-title {
  font-size: 1rem;
  font-weight: 700;
  color: #0d4f73;
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: 6px;
  margin-bottom: 12px;
}
.table-sm td { vertical-align: middle; }

.ska-scroll .card-body label.small::after,
.ska-scroll .card-body label.fw-bold::after {
  content: " *";
  color: #dc3545;
  font-weight: 700;
}

.ska-required-missing {
  border-color: #dc3545 !important;
  background: #fff5f5 !important;
  box-shadow: 0 0 0 0.12rem rgba(220, 53, 69, 0.2) !important;
}

.ska-required-missing-group {
  border: 1px solid #dc3545;
  border-radius: 6px;
  padding: 8px 10px;
  background: #fff5f5;
}
</style>
